/**
 * AT-13 — canonical install-consent copy module.
 *
 * Single source of truth for the install-consent surface strings rendered
 * BOTH by the Onboarding install-consent phase AND the Settings Disclosure
 * tab. AT-13 acceptance requires byte-equivalence between the two —
 * importing from this module is the structural guarantee.
 *
 * Copy is verbatim per design §4.6 Demo 5. Editing requires coordinated
 * design-doc + risk-register updates (see AT-13 Notes).
 *
 * Version pins (`v3.0.0`, `v1.0.0`, `slice-1-v1`) are normative for Slice
 * 1; bumping them is OD-S1-19 (deferred to Slice 3).
 */

export const HOW_IT_WORKS_URL = 'https://ownyou.app/how-it-works';
export const HOW_IT_WORKS_ANONYMITY_ANCHOR_URL =
  'https://ownyou.app/how-it-works#anonymity-and-pii';
export const POLICY_JSON_GITHUB_URL =
  'https://github.com/nlongcn/ownyou-consumer-application/blob/master/docs/policies/disclosure-policy-v3.json';

export interface InstallConsentVersions {
  protocol_version: 'slice-1-v1';
  disclosure_policy_version: 'v3.0.0';
  trust_registry_version: 'v1.0.0';
}

export const INSTALL_CONSENT_VERSIONS: InstallConsentVersions = {
  protocol_version: 'slice-1-v1',
  disclosure_policy_version: 'v3.0.0',
  trust_registry_version: 'v1.0.0',
};

export interface ShareCategory {
  label: 'Interests' | 'Purchase Intent' | 'Demographics' | 'Household profile';
  detail: string;
}

export const INSTALL_CONSENT_SHARES: readonly ShareCategory[] = [
  {
    label: 'Interests',
    detail: 'IAB Audience Taxonomy 1.1 (segments above confidence threshold)',
  },
  {
    label: 'Purchase Intent',
    detail: 'IAB Audience Taxonomy 1.1',
  },
  {
    label: 'Demographics',
    detail: 'age range, gender, marital status, etc.',
  },
  {
    label: 'Household profile',
    detail: 'number of adults / children, property type, etc.',
  },
] as const;

export const INSTALL_CONSENT_ANONYMITY_STATEMENT =
  'You remain anonymous — your name, email, wallet address, browsing history, and any other personally identifiable data are stripped before anything leaves your device.';

export const INSTALL_CONSENT_DEFAULTS_NOTE =
  'These are the protocol defaults. Granular controls (turning categories off, raising the confidence threshold) land in a future release.';

export const INSTALL_CONSENT_TESTNET_NOTE =
  'Earnings are paid in Base Sepolia testnet USDC — not real money during this phase.';

export const INSTALL_CONSENT_COPY = {
  headline: 'Welcome to OwnYou',
  shareIntro: 'Your wallet will share the following with publishers and advertisers:',
  anonymityStatement: INSTALL_CONSENT_ANONYMITY_STATEMENT,
  learnHowLabel: 'Learn how',
  defaultsNote: INSTALL_CONSENT_DEFAULTS_NOTE,
  testnetNote: INSTALL_CONSENT_TESTNET_NOTE,
  continueButtonLabel: 'Continue',
  policyJsonLinkLabel: 'view policy JSON on GitHub',
  whyDefaultsLinkLabel: 'Why these defaults?',
} as const;
